import { z } from 'zod';
import { uuidV7Schema } from './ids';

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u);
export const workingWindowSchema = z
  .object({ opensAt: time, closesAt: time })
  .strict()
  .refine(
    (value) => value.opensAt < value.closesAt,
    'Closing time must follow opening time.'
  );
export const weeklyWindowSchema = z
  .object({ day: z.number().int().min(0).max(6), ...workingWindowSchema.shape })
  .strict()
  .refine(
    (value) => value.opensAt < value.closesAt,
    'Closing time must follow opening time.'
  );
export const adviserHoursSchema = z
  .array(weeklyWindowSchema)
  .max(21)
  .refine(
    (hours) =>
      hours.every((entry, index) =>
        hours.every(
          (other, position) =>
            position === index ||
            entry.day !== other.day ||
            entry.closesAt <= other.opensAt ||
            other.closesAt <= entry.opensAt
        )
      ),
    'Working windows must not overlap.'
  );
export const timezoneSchema = z
  .string()
  .min(1)
  .max(100)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value }).format();
      return true;
    } catch {
      return false;
    }
  }, 'A valid timezone is required.');
export const availabilityExceptionSchema = z
  .object({ date: z.iso.date(), windows: z.array(workingWindowSchema).max(3) })
  .strict()
  .refine(
    (value) =>
      value.windows.every((entry, index) =>
        value.windows.every(
          (other, position) =>
            position === index ||
            entry.closesAt <= other.opensAt ||
            other.closesAt <= entry.opensAt
        )
      ),
    'Exception windows must not overlap.'
  );
export const adviserInputSchema = z
  .object({
    id: uuidV7Schema.optional(),
    expectedRevision: uuidV7Schema.nullable(),
    name: z.string().trim().min(1).max(150),
    email: z.email().max(254).nullable(),
    active: z.boolean(),
    timezone: timezoneSchema,
    weeklyHours: adviserHoursSchema,
    exceptions: z
      .array(availabilityExceptionSchema)
      .max(100)
      .refine(
        (values) =>
          new Set(values.map((value) => value.date)).size === values.length,
        'One exception per date.'
      ),
    botIds: z
      .array(uuidV7Schema)
      .max(100)
      .refine((values) => new Set(values).size === values.length),
  })
  .strict();
export type AdviserInput = z.infer<typeof adviserInputSchema>;
export const adviserSchema = adviserInputSchema
  .omit({ expectedRevision: true })
  .extend({ id: uuidV7Schema, revision: uuidV7Schema })
  .strict();
export type Adviser = z.infer<typeof adviserSchema>;
export const adviserCalendarInputSchema = z
  .object({
    connectionId: uuidV7Schema,
    calendarId: z.string().min(1).max(500),
  })
  .strict();
export const adviserCalendarSchema = adviserCalendarInputSchema
  .extend({
    id: uuidV7Schema,
    name: z.string().min(1),
    writable: z.boolean(),
    provider: z.enum(['google', 'microsoft']),
    address: z.email(),
  })
  .strict();
export type AdviserCalendar = z.infer<typeof adviserCalendarSchema>;
export const adviserDirectorySchema = z
  .object({
    advisers: z.array(adviserSchema),
    nextCursor: uuidV7Schema.nullable(),
  })
  .strict();
export const adviserDetailSchema = z
  .object({ adviser: adviserSchema, calendars: z.array(adviserCalendarSchema) })
  .strict();
export const eligibleAdvisersSchema = z
  .object({
    advisers: z.array(adviserDetailSchema),
    nextCursor: uuidV7Schema.nullable(),
  })
  .strict();
export const calendarAccountSchema = z
  .object({
    id: uuidV7Schema,
    provider: z.enum(['google', 'microsoft']),
    address: z.email(),
    status: z.enum(['ready', 'error', 'disabled']),
    error: z.string().nullable(),
  })
  .strict();
export const calendarAccountsSchema = z
  .object({ connections: z.array(calendarAccountSchema) })
  .strict();

export const adviserPoolSchema = z
  .object({
    id: uuidV7Schema,
    members: z
      .array(
        z
          .object({ adviserId: uuidV7Schema, bookingCalendarId: uuidV7Schema })
          .strict()
      )
      .min(1)
      .max(50)
      .refine(
        (values) =>
          new Set(values.map((value) => value.adviserId)).size === values.length
      ),
    noticeMinutes: z.number().int().min(0).max(43_200),
    bufferBeforeMinutes: z.number().int().min(0).max(240),
    bufferAfterMinutes: z.number().int().min(0).max(240),
    horizonDays: z.number().int().min(1).max(90),
  })
  .strict();
